import { BehaviorSubject, map, Observable } from 'rxjs';
import { PaginationQueryDto } from '../data-transfer/entities/pagination-query-dto';
import { PaginationResponseDto } from '../data-transfer/entities/pagination-response-dto';
import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { PaginationFilterDto } from '../data-transfer/entities/pagination-filter-dto';

export type PaginationEndpoint<T> = (query: PaginationQueryDto) => Observable<PaginationResponseDto<T>>;

export class PaginationDataSource<T> extends DataSource<T> {
  private loading = new BehaviorSubject<boolean>(true);
  private paginationQuery: PaginationQueryDto;

  private pageSubject!: BehaviorSubject<PaginationResponseDto<T>>;

  private filteredColumnsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  constructor(private endpoint: PaginationEndpoint<T>) {
    super();

    this.paginationQuery = {
      pageIndex: 0,
      pageSize: 1,
      sortByField: undefined,
      sortAscending: false,
      filters: [],
    };

    this.pageSubject = new BehaviorSubject<PaginationResponseDto<T>>({
      pageIndex: 0,
      totalPages: 1,
      totalItemCount: 0,
      items: [],
      allIds: [],
    });
  }

  connect(collectionViewer: CollectionViewer): Observable<readonly T[]> {
    return this.pageSubject.pipe(map(x => x.items));
  }

  disconnect(collectionViewer: CollectionViewer): void {}

  get data(): T[] {
    return this.pageSubject.value.items;
  }

  get page(): Observable<PaginationResponseDto<T>> {
    return this.pageSubject;
  }

  get filteredColumns(): BehaviorSubject<string[]> {
    return this.filteredColumnsSubject;
  }

  get isLoading(): Observable<boolean> {
    return this.loading;
  }

  get filters() {
    return this.paginationQuery.filters;
  }

  addFilter(fieldName: string, value: any) {
    this.addFilterOptions(fieldName, [value]);
  }

  addFilterOptions(fieldName: string, options: any[]) {
    this.paginationQuery.filters = this.paginationQuery.filters.filter(x => x.fieldName != fieldName);

    for (const value of options) {
      if (value !== undefined) {
        const filter: PaginationFilterDto = {
          fieldName: fieldName,
          value: value === null ? null : value.toString(),
        };

        this.paginationQuery.filters.push(filter);
      }
    }

    this.paginationQuery.pageIndex = 0;
    this.refresh();
  }

  sort(fieldName: string, ascending: boolean) {
    this.paginationQuery.sortByField = fieldName;
    this.paginationQuery.sortAscending = ascending;

    this.refresh();
  }

  removeSorting() {
    this.paginationQuery.sortByField = undefined;
    this.refresh();
  }

  refresh() {
    this.loading.next(true);
    this.endpoint(this.paginationQuery).subscribe(result => {
      this.pageSubject.next(result);
      this.filteredColumns.next(this.paginationQuery.filters.map(x => x.fieldName));
      this.loading.next(false);
    });
  }

  clear() {
    this.paginationQuery.filters = [];
    this.pageSubject.next({
      pageIndex: 0,
      totalPages: 1,
      totalItemCount: 0,
      items: [],
      allIds: [],
    });
    this.loading.next(false);
  }

  fetchData(pageIndex: number, pageSize: number, filters: PaginationFilterDto[] | undefined = undefined) {
    this.paginationQuery.pageIndex = pageIndex;
    this.paginationQuery.pageSize = pageSize;

    if (filters) {
      this.paginationQuery.filters = filters;
    }

    this.refresh();
  }

  setLoading(value: boolean): void {
    this.loading.next(value);
  }
}
